import { apiConfig } from '../auth';
import { AreaConfig, ConversationStartResponse, ChatRequest, StreamChunk, CardActionRequest } from '../types';

class AgentService {
  private getAuthHeader = async (getAccessToken: () => Promise<string>): Promise<HeadersInit> => {
    const token = await getAccessToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  async getAreas(getAccessToken: () => Promise<string>): Promise<AreaConfig[]> {
    const headers = await this.getAuthHeader(getAccessToken);
    const response = await fetch(`${apiConfig.baseUrl}/areas`, { headers });
    
    if (!response.ok) {
      throw new Error('Failed to fetch areas');
    }
    
    return response.json();
  }

  async getArea(areaId: string, getAccessToken: () => Promise<string>): Promise<AreaConfig> {
    const headers = await this.getAuthHeader(getAccessToken);
    const response = await fetch(`${apiConfig.baseUrl}/areas/${areaId}`, { headers });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch area: ${areaId}`);
    }
    
    return response.json();
  }

  async startConversation(getAccessToken: () => Promise<string>): Promise<ConversationStartResponse> {
    const headers = await this.getAuthHeader(getAccessToken);
    const response = await fetch(`${apiConfig.baseUrl}/chat/start`, {
      method: 'POST',
      headers,
    });
    
    if (!response.ok) {
      throw new Error('Failed to start conversation');
    }
    
    return response.json();
  }

  async sendMessage(
    request: ChatRequest,
    getAccessToken: () => Promise<string>,
    onChunk: (chunk: StreamChunk) => void
  ): Promise<void> {
    const headers = await this.getAuthHeader(getAccessToken);
    
    const response = await fetch(`${apiConfig.baseUrl}/chat/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Failed to send message');
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      
      // Parse SSE data
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            onChunk(data as StreamChunk);
          } catch (e) {
            console.error('Failed to parse SSE data:', e);
          }
        }
      }
    }
  }

  async sendCardAction(
    request: CardActionRequest,
    getAccessToken: () => Promise<string>,
    onChunk: (chunk: StreamChunk) => void
  ): Promise<void> {
    const headers = await this.getAuthHeader(getAccessToken);
    
    const response = await fetch(`${apiConfig.baseUrl}/chat/card-action`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error('Failed to send card action');
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      
      // Parse SSE data
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            onChunk(data as StreamChunk);
          } catch (e) {
            console.error('Failed to parse SSE data:', e);
          }
        }
      }
    }
  }
}

export const agentService = new AgentService();
